
import {map} from 'rxjs/operators';
import { Injectable, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { Observable ,  BehaviorSubject } from "rxjs";

import { PaymentItem, PaymentSearchCriteria, ProofOfPurchaseTypes, ApplicationPayment, PaymentRecordAdjustmentRequest, PaymentRecordAdjustmentRequestSearchSpec, AuthorizationResponse } from "../models/paymentItem.model"
import { htmlEscape } from "../../Helpers/UtilityFunctions";
import { GenerateReportModel, RegenerateRequeueRequest, InvoiceReceiptRegenerateResult } from "../../reports/model/reports.model";

@Injectable()
export class PaymentService implements OnInit {
    // This authorization request is what is being filled in
    //      as user "purchases", and contains values being sent to CBOSS CPP
    //      as well as values being logged
    currentAuthorizationRequest = new BehaviorSubject<AuthorizationRequest>(new AuthorizationRequest(null, null, null));
    currentAuthorizationRequest$ = this.currentAuthorizationRequest.asObservable();
    updateCurrentAuthorizationRequest(data: AuthorizationRequest) {
        if (this.currentAuthorizationRequest.getValue() !== data)
            this.currentAuthorizationRequest.next(data);
    }

    static BaseUrl: string = "/api/payment";

    // which of the below "paymentTypes" is selected
    paymentTypeSelected: any;
    paymentTypes = [
        { "name": " ", "value": "" },
        { "name": "Mail In Check", "value": "LocalCheck" },
        { "name": "Credit Card", "value": "Credit" },
        { "name": "ACH", "value": "Check" }
    ];  
    // which of the below "creditCards" is selected
    creditCardSelected: any;

    constructor(
        public readonly http: HttpClient,
        public readonly router: Router
    ) {
    }

    ngOnInit() {
    }

    deletePaymentsForApplication(authorization: AuthorizationRequest): Observable<string> {
        return this.http.post<string>("/api/Payments/deleteByApplication/", authorization);
    }

    // Used to populate selects (front end) 
    //  and authorizationRequest.accountInformation.paymentType (backend)
   

    // Function to begin the process of submitting to CBOSS
    

    // Function to decrypt a success/failure token from the CBoss CPP
    detokenizeResponse(token: string) {
        return this.http.get(`/api/Payment/DetokenizeResponse/${token}`).pipe(
            map((response) => {
                var retValString = "";
                var result = response as any;
                result.split("&").forEach((element: any) => {
                    retValString += `"${element.split("=")[0]}": "${element.split("=")[1]}",`;
                });
                retValString = retValString.slice(0, -1);
                retValString = retValString.replace("+", " ");
                retValString = `{${retValString}}`;
                return unescape(retValString);
            }))
            .toPromise();
    }

    getPdf(htmlModel: GenerateReportModel, pdfType: string): Observable<string> {
        const url = (pdfType === ProofOfPurchaseTypes.Receipt) ? "/api/Payment/generateReceipt/" : "/api/Payment/generateInvoice/";
        return this.http.post<string>(url, htmlModel);
    }

    redirectToCBoss(): Observable<any> {
        // Call Controller to retrieve URL
        const cbossUrl = "/api/Payment/RetrieveRedirectUrl/";
        const temp = this.currentAuthorizationRequest.getValue();
        temp.flowUrl = htmlEscape(window.location.href);
        this.updateCurrentAuthorizationRequest(temp);
        return this.http.post<string>(cbossUrl, this.currentAuthorizationRequest.getValue());
    }

    sendToCBoss(req: AuthorizationRequest): Observable<any> {
        const cbossUrl = "/api/Payment/RetrieveRedirectUrl/";
        return this.http.post<string>(cbossUrl, req);
    }


    batchReproduceInvoicesAndReceipts(requests: RegenerateRequeueRequest[]): Observable<InvoiceReceiptRegenerateResult[]> {
        return this.http.post<InvoiceReceiptRegenerateResult[]>("/api/Payment/BatchRegenerateDocuments", requests);
    }

    reproduceInvoiceOrReceipt(request: RegenerateRequeueRequest): Observable<string> {
        return this.http.post<string>("/api/Payment/RegenerateDocument", request);
    }

    requestPaymentAdjustment(request: PaymentRecordAdjustmentRequest): Observable<PaymentRecordAdjustmentRequest> {
        return this.http.post<PaymentRecordAdjustmentRequest>("/api/Payments/request-adjustment", request);
    }

    savePaymentAdjustment(request: PaymentRecordAdjustmentRequest): Observable<PaymentRecordAdjustmentRequest> {
        return this.http.post<PaymentRecordAdjustmentRequest>("/api/Payments/save-adjustment", request);
    }

    savePaymentRecord(payment: ApplicationPayment): Observable<ApplicationPayment> {
        return this.http.post<ApplicationPayment>("/api/Payment/SavePayment", payment);
    }

    savePaymentRequest(organization: string, request: AuthorizationRequest): Observable<AuthorizationRequest> {
        return this.http.post<AuthorizationRequest>(`/api/Payment/SavePaymentAuthorization/${organization}`, request);
    }

    search(criteria: PaymentSearchCriteria): Observable<ApplicationPayment[]> {
		return this.http.post<ApplicationPayment[]>(`/api/Payments/search`, criteria);
    }

    findAdjustmentRequest(requestId: string): Observable<PaymentRecordAdjustmentRequest> {
        return this.http.get<PaymentRecordAdjustmentRequest>(`/api/Payments/find-adjustment/${requestId}`);
    }

    searchAdjustmentRequests(spec: PaymentRecordAdjustmentRequestSearchSpec): Observable<PaymentRecordAdjustmentRequest[]> {
        return this.http.post<PaymentRecordAdjustmentRequest[]>("/api/Payments/search-adjustments", spec);
    }

    private _buildQueryString(criteria: PaymentSearchCriteria): string {
        const params = new URLSearchParams();
        for (let key in criteria) {
            if (criteria.hasOwnProperty(key)) {
                const value = criteria[key];
                if (!value) continue;
                if (value instanceof Array) {
                    for (let index in value) {
                        if (value.hasOwnProperty(index)) {
                            params.set(key + "[" + index + "]", value[index]);
                        }
                    }
                } else if (value instanceof Date) {
                    params.set(key, value.toISOString());
                } else {
                    params.set(key, value.toString());
                }
            }
        }
        return params.toString();
    }
}

// The request sent to CBOSS CPP service. Contains all information
export class AuthorizationRequest {
    accountInformation: AccountInformation;
    applicationNumber: string = "";
    applicationType: string = "";
    billingAddressInformation: BillingAddressInformation;
    cancelUrl: string;
    failUrl: string;
    flowUrl: string = "";
    paymentInformation: PaymentInformation;
    successUrl: string;
    
    constructor(accountInformation: AccountInformation,
        billingAddress: BillingAddressInformation,
        paymentInformation: PaymentInformation) {
        this.accountInformation = (accountInformation) ? accountInformation : new AccountInformation();
        this.billingAddressInformation = (billingAddress) ? billingAddress : new BillingAddressInformation();
        this.paymentInformation = new PaymentInformation();
        if (paymentInformation) {
            this.paymentInformation.amount = paymentInformation.amount;
            this.paymentInformation.identifier = paymentInformation.identifier;
            this.paymentInformation.amountLine1 = paymentInformation.amountLine1;
            this.paymentInformation.amountLine2 = paymentInformation.amountLine2;
            this.paymentInformation.orderNumber = paymentInformation.orderNumber;
            this.paymentInformation.comment1 = paymentInformation.comment1;
            this.paymentInformation.comment2 = paymentInformation.comment2;
            this.paymentInformation.paymentItems = paymentInformation.paymentItems;
        } else {
            this.paymentInformation.amount = 0.00;
        }
    }
}

// Account Information
export class AccountInformation {
    paymentType: string;     // "Credit", "Cash", "Check"
    creditCardCvvCode: string;
    creditCardExpirationMonth: number;
    creditCardExpirationYear: number;
    creditCardNumber: string;
    bankAccountNumber: string;
    cashReceiptNumber: string;
}

// Customer billing information
export class BillingAddressInformation {
    addressLine1: string;
    addressLine2: string;
    city: string;
    country: string;
    emailAddress: string;
    firstName: string;
    lastOrBusinessName: string;
    middleName: string;
    phoneNumber: string;
    state: string;
    zip: string;
}

export class PaymentInformation {

    // User inputs gathered during application use
    amount: number;
    identifier: string;
    amountLine1: number;
    amountLine2: number;
    orderNumber: string;
    comment1: string;
    comment2: string;
    paymentItems: PaymentItem[] = [];


    // Functions
    calculateTotal(): string {
        this.amount = 0;
        for (let i = 0; i < this.paymentItems.length; i++) {
            // for each item, multiply price x quantity and add to total amount
            this.amount += (this.paymentItems[i].fee);
        }
        // Update observable
        //this.updateFormattedAmount(this.amount.toFixed(2).toString());
        return this.amount.toFixed(2);
    }

    // Add a payment Item
    addItem(paymentItem: PaymentItem) {
        this.paymentItems.push(paymentItem);
        this.calculateTotal();
    }

    addItems(paymentItems: PaymentItem[]) {
        this.paymentItems = this.paymentItems.concat(paymentItems);
        this.calculateTotal();
    }

    // Remove a payment Item
    removeItem(paymentItemInformation: PaymentItemInformation) {
        const foundItem = this.paymentItems.find(x => x.id === paymentItemInformation.description);
        if (foundItem) {
            const index = this.paymentItems.indexOf(foundItem);
            this.paymentItems.splice(index, 1);
            this.calculateTotal();
        }
    }

    clearPurchases() {
        this.paymentItems = [];
        this.calculateTotal();
    }

    // Increase the quantity of a specific payment item
    increaseQuantity(paymentItemInforation: PaymentItemInformation) {
        const foundItem = this.paymentItems.find(x => x.id === paymentItemInforation.description);
        if (foundItem) {
            //foundItem.quantity++;
            this.calculateTotal();
        }
    }

    // Decrease the quantity of a specific payment item
    decreaseQuantity(paymentItemInformation: PaymentItemInformation) {
        const foundItem = this.paymentItems.find(x => x.id === paymentItemInformation.description);
        if (foundItem) {
            //foundItem.quantity--;
            // check for zero quantity
            //if (foundItem.quantity === 0) {
            //    this.removeItem(paymentItemInformation);
            //}
            this.calculateTotal();
        }
    }

}

// Bare basic unit/item purchased
export class PaymentItemInformation {
    description: string;
    price: number;
    quantity: number;
    legalReference: string;
    department: string;
    fund: string;
    ali: string;


    constructor() {
        this.price = 0.00;
        this.quantity = 1;
    }
}



export class PaymentReceipt {
    PaymentItems: PaymentItem[] = [];
    CompanyName: string;
    ApplicationNumber: string;
    PaymentDate: string;
    authorizationResponse: AuthorizationResponse;
}

export function calculateTotal(payments: PaymentItem[]): string {
        let amount = 0;
        for (let i = 0; i < payments.length; i++) {
            amount += (payments[i].fee);
        }
        return amount.toFixed(2);
}
